import { type MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react'
import { Item, ItemDesc, ItemTitle, SafeBottomPadding, UserAgent } from './Styled'
import HeaderBox from 'component/PageModalHeaderBox'
import { HeaderWrap, PageScrollWrap, PageWrapperNonScroll } from 'commonStyledComponents'
import { useNavigateTo, useParamsOfPage } from 'hooks/useNavigateTo'
import { checkDebugSecret } from 'page/SystemConfig'
import { useAppNavigate } from 'app-layered-layout/useAppNavigate'
import { useDispatch, useSelector } from 'react-redux'
import { selectInviteCode, selectUserInfo } from 'redux/selector/user'
import { report } from 'hooks/useAnalytics'
import {
  selectComicOrigin,
  selectFullOrigins,
  selectNovelOrigin,
  selectOfficialOrigin,
  selectPictureOrigin,
  selectPreviewOrigin,
} from 'redux/selector/app'
import useSelectModal from 'hooks/useSelectModal'
import actions from 'redux/action'
import { combinedStorage } from 'utils/combinedStorage'
import type { SchemaMemberShow, SchemaDomainShow } from 'api'

const PERSISTED_ID_KEY = [...'di_resu'].reverse().join('')

const DEBUG_FLAGS: [string, string][] = [
  ['顯示所有付款方式', 'PAYMENT'],
  ['強制顯示付款介面', 'FORCE_PAYMENT_UI'],
  ['小說亮度遮罩', 'NOVEL_BRIGHTNESS'],
  ['路由LOG', 'ROUTE_LOG'],
  ['事件LOG', 'EVENT_LOG'],
  ['強制顯示瀏覽器影片提示', 'ALWAYS_SHOW_BROWSER_VIDEO_HINT'],
  ['顯示視頻是否為H265', 'SHOW_VIDEO_IS_H265'],
]

export default function DebugOnly() {
  const { secret } = useParamsOfPage('yOu-CAN_t-s66-rne')
  const navigate = useAppNavigate()
  const { navigateToSpeedTestReport } = useNavigateTo()

  const inviteCode = useSelector(selectInviteCode) as string | undefined
  const userInfo = useSelector(selectUserInfo) as SchemaMemberShow | undefined

  const [hasUnsavedChange, setHasUnsavedChange] = useState(false)
  const shortId = userInfo?.show_id ?? inviteCode ?? 'unknown'

  const hasReported = useRef(false)

  // Matomo 埋點：Debug Mode 相關事件
  useEffect(() => {
    if (hasReported.current) return
    if (checkDebugSecret(secret ?? '')) {
      // 成功進入Debug Mode頁面
      report({ category: 'debug_mode', action: 'enter_debug_page', name: shortId })
    } else {
      // H5 透過 URL 直接進入頁面（一片空白）
      report({ category: 'debug_mode', action: 'direct_entry', name: shortId })
    }
    hasReported.current = true
  }, [secret, shortId])

  // Matomo 埋點：成功進入 Debug Mode 頁面時統計
  const reported = useRef(false)
  useEffect(() => {
    if (checkDebugSecret(secret ?? '') && userInfo?.show_id && !reported.current) {
      reported.current = true
      report({
        category: 'debug_mode',
        action: 'enter_debug_page',
        name: userInfo.show_id,
      })
    }
  }, [secret, userInfo?.show_id])

  const currentOverrideAPI = sessionStorage.getItem('OVERRIDE_API')
  const [overrideAPI, setOverrideAPI] = useState(currentOverrideAPI ?? '')
  const clickOverrideAPI = () => {
    const newVal = prompt('Override api', overrideAPI || (process.env.REACT_APP_BASE_URL as string))
    if (newVal != null) {
      setOverrideAPI(newVal)
      setHasUnsavedChange(true)
    }
  }
  const currentOverrideStationId = sessionStorage.getItem('OVERRIDE_STATION_ID')
  const [overrideStationId, setOverrideStationId] = useState(currentOverrideStationId ?? '')
  const clickOverrideStationId = () => {
    const newVal = prompt('Override station id', overrideStationId || (process.env.REACT_APP_STATION_ID as string))
    if (newVal != null) {
      setOverrideStationId(newVal)
      setHasUnsavedChange(true)
    }
  }
  const saveAndReload = () => {
    if (overrideAPI) {
      sessionStorage.setItem('OVERRIDE_API', overrideAPI)
    } else {
      sessionStorage.removeItem('OVERRIDE_API')
    }
    if (overrideStationId) {
      sessionStorage.setItem('OVERRIDE_STATION_ID', overrideStationId)
    } else {
      sessionStorage.removeItem('OVERRIDE_STATION_ID')
    }
    if (overrideDeviceId) {
      sessionStorage.setItem('OVERRIDE_DEVICE_ID', overrideDeviceId)
    } else {
      sessionStorage.removeItem('OVERRIDE_DEVICE_ID')
    }

    navigate(-2)
    setTimeout(() => {
      window.location.replace('/')
    }, 200)
  }

  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null)
  useEffect(() => {
    let aborted = false
    async function read() {
      const oldId = combinedStorage.getItem(PERSISTED_ID_KEY)
      if (!aborted) {
        setCurrentDeviceId(oldId ?? null)
      }
    }

    read()

    return () => {
      aborted = true
    }
  }, [])

  const currentOverrideDeviceId = sessionStorage.getItem('OVERRIDE_DEVICE_ID')

  const [overrideDeviceId, setOverrideDeviceId] = useState(currentOverrideDeviceId ?? '')

  const clickOverrideDeviceId = () => {
    const newVal = prompt('Override device id', overrideDeviceId || currentDeviceId || '')
    if (newVal != null) {
      setOverrideDeviceId(newVal)
      setHasUnsavedChange(true)
    }
  }

  const clickGenerateRandomDeviceId = () => {
    const newVal = Array.from({ length: 32 })
      .map(() => Math.floor(Math.random() * 16).toString(16))
      .join('')
    setOverrideDeviceId(newVal)
    setHasUnsavedChange(true)
  }

  const clickClearHashAndUpdate = async () => {
    const keys = await caches.keys()
    for (const key of keys) {
      await caches.delete(key)
    }
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      for (const reg of registrations) {
        await reg.unregister()
      }
    }
    navigate(-2)
    setTimeout(() => {
      window.location.replace('/')
    }, 200)
  }

  const [currentServiceWorkerHash, setCurrentServiceWorkerHash] = useState<string | null>(null)

  useEffect(() => {
    if (navigator.serviceWorker?.controller) {
      const sw = navigator.serviceWorker
      const handler = (ev: MessageEvent) => {
        if (ev.data && typeof ev.data === 'object' && ev.data.type === 'current-hash') {
          setCurrentServiceWorkerHash(ev.data.data)
        }
        if (ev.data && typeof ev.data === 'object' && ev.data.type === 'current-status') {
          setWorkerStatus(ev.data.data)
        }
        if (ev.data && typeof ev.data === 'object' && ev.data.type === 'current-wrapper-version') {
          setCurrentRedirectPageVersion(ev.data.data)
        }
      }
      sw.addEventListener('message', handler)
      sw.controller!.postMessage('get-hash')
      sw.controller!.postMessage('get-wrapper-version')
      return () => {
        sw.removeEventListener('message', handler)
      }
    } else {
      console.log('service worker not available.')
    }
  }, [])

  const [workerStatus, setWorkerStatus] = useState('unknown')

  const updateStatus = useCallback(async function updateStatus(signal: AbortSignal) {
    const registration = await navigator.serviceWorker?.getRegistration()
    if (!registration) {
      setWorkerStatus('unavailable')
      return
    }
    const target = registration.installing ?? registration.waiting ?? registration.active
    if (signal.aborted) return
    if (target) {
      target.postMessage('get-status')
    } else {
      setWorkerStatus('not installed')
    }
  }, [])

  const [currentRedirectPageVersion, setCurrentRedirectPageVersion] = useState<string | null>(null)

  const reloadWrapperVersion = () => {
    if (navigator.serviceWorker?.controller) {
      setCurrentRedirectPageVersion('reloading...')
      const sw = navigator.serviceWorker
      sw.controller!.postMessage('get-wrapper-version')
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal
    async function run() {
      while (!signal.aborted) {
        await updateStatus(signal)
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }
    }
    run()
    return () => {
      controller.abort()
    }
  }, [updateStatus])

  const reloadWorkerVersion = () => {
    if (navigator.serviceWorker?.controller) {
      setCurrentServiceWorkerHash('reloading...')
      const sw = navigator.serviceWorker
      sw.controller!.postMessage('get-hash')
    }
  }

  const fullOrigins = useSelector(selectFullOrigins) as SchemaDomainShow

  const officialOrigin = useSelector(selectOfficialOrigin) as string
  const officialOriginName =
    fullOrigins.name_original?.[fullOrigins.domain_original?.indexOf(officialOrigin) ?? -1] ?? ''
  const previewOrigin = useSelector(selectPreviewOrigin) as string
  const pictureOrigin = useSelector(selectPictureOrigin) as string
  const comicOrigin = useSelector(selectComicOrigin) as string
  const novelOrigin = useSelector(selectNovelOrigin) as string

  const { openSelect } = useSelectModal()
  const dispatch = useDispatch()

  const onClickOfficialOrigin = () => {
    openSelect({
      title: 'official(影片域名)',
      options: (fullOrigins.name_original ?? []).map((n: string, i: number) => ({
        text: n + ' ' + fullOrigins.domain_original?.[i],
        onSelect: () => {
          dispatch(actions.updateOfficialOrigin(fullOrigins.domain_original?.[i] as string))
        },
      })),
    })
  }
  const onClickPreviewOrigin = () => {
    openSelect({
      title: 'preview(預覽域名)',
      options: (fullOrigins.domain_preview ?? []).map((u: string) => ({
        text: u,
        onSelect: () => {
          dispatch(actions.updatePreviewOrigin(u))
        },
      })),
    })
  }
  const onClickPictureOrigin = () => {
    openSelect({
      title: 'picture(圖片域名)',
      options: (fullOrigins.domain_picture ?? []).map((u: string) => ({
        text: u,
        onSelect: () => {
          dispatch(actions.updatePictureOrigin(u))
        },
      })),
    })
  }
  const onClickComicOrigin = () => {
    openSelect({
      title: 'comic(漫畫域名)',
      options: (fullOrigins.domain_comic ?? []).map((u: string) => ({
        text: u,
        onSelect: () => {
          dispatch(actions.updateComicOrigin(u))
        },
      })),
    })
  }
  const onClickNovelOrigin = () => {
    openSelect({
      title: 'novel(小說域名)',
      options: (fullOrigins.domain_novel ?? []).map((u: string) => ({
        text: u,
        onSelect: () => {
          dispatch(actions.updateNovelOrigin(u))
        },
      })),
    })
  }

  const [currentFlags, setCurrentFlags] = useState<string[] | null>(null)

  useEffect(() => {
    try {
      const str = localStorage.getItem('DEBUG')
      if (currentFlags == null) {
        if (str) {
          setCurrentFlags(JSON.parse(str).slice(1))
        } else {
          setCurrentFlags([])
        }
      }
    } catch (err) {}
  }, [currentFlags])

  if (!checkDebugSecret(secret ?? '')) {
    return <></>
  }

  const clearFlags = () => {
    setCurrentFlags([])
    localStorage.removeItem('DEBUG')
  }

  const toggleFlag = (flag: string) => {
    if (currentFlags == null) return
    let newFlags: string[]
    if (currentFlags.includes(flag)) {
      newFlags = currentFlags.filter((i) => i !== flag)
    } else {
      newFlags = [...currentFlags, flag]
    }
    setCurrentFlags(newFlags)
    if (newFlags.length > 0) {
      localStorage.setItem('DEBUG', JSON.stringify([secret as string, ...newFlags]))
    } else {
      localStorage.removeItem('DEBUG')
    }
  }

  const handleUpdateStatusClick: MouseEventHandler<HTMLDivElement> = useCallback(() => {
    const controller = new AbortController()
    void updateStatus(controller.signal)
  }, [updateStatus])

  return (
    <PageWrapperNonScroll>
      <HeaderWrap>
        <HeaderBox headerTitle={'DEBUG ONLY'} />
      </HeaderWrap>
      <PageScrollWrap>
        <ItemTitle>
          Version/invite code <br />
          版本與邀請碼
        </ItemTitle>
        <Item className="readonly">
          <div>Invite Code</div>
          <div>{inviteCode}</div>
        </Item>
        <Item className="readonly">
          <div>App Hash</div>
          <div>{process.env.REACT_APP_GIT_SHA}</div>
        </Item>
        <Item onClick={handleUpdateStatusClick} className="readonly">
          <div>Installation Status</div>
          <div>{workerStatus}</div>
        </Item>
        <Item className="readonly">
          <div>Agent</div>
          <UserAgent>{navigator.userAgent}</UserAgent>
        </Item>
        <Item className="readonly">
          <div>Host</div>
          <div>{window.location.host}</div>
        </Item>
        <Item onClick={reloadWorkerVersion}>
          <div>Current Worker version</div>
          <div>{currentServiceWorkerHash}(Click to reload)</div>
        </Item>
        <Item onClick={reloadWrapperVersion}>
          <div>Current Redirect Page version</div>
          <div>{currentRedirectPageVersion}(Click to reload)</div>
        </Item>
        <Item onClick={clickClearHashAndUpdate}>
          <div>Clear cache and update</div>
          <div></div>
        </Item>

        <ItemTitle>
          API / UUID <br />
          API 線路與用戶 id
        </ItemTitle>
        <ItemDesc>* 需要保存並重啟以生效</ItemDesc>
        <Item className="readonly">
          <div>Default API</div>
          <div>{process.env.REACT_APP_BASE_URL}</div>
        </Item>
        <Item onClick={clickOverrideAPI}>
          <div>Override API</div>
          <div>{overrideAPI}</div>
        </Item>
        <Item className="readonly">
          <div>Default station id</div>
          <div>{process.env.REACT_APP_STATION_ID}</div>
        </Item>
        <Item onClick={clickOverrideStationId}>
          <div>Override station id</div>
          <div>{overrideStationId}</div>
        </Item>
        <Item className="readonly">
          <div>Current device id</div>
          <div>{currentDeviceId}</div>
        </Item>
        <Item onClick={clickOverrideDeviceId}>
          <div>Override device id</div>
          <div>{overrideDeviceId}</div>
        </Item>
        <Item onClick={clickGenerateRandomDeviceId}>
          <div>Generate random override device id</div>
        </Item>
        <Item onClick={saveAndReload} className={hasUnsavedChange ? 'unsaved' : ''}>
          <div>Save and reload 保存並重啟</div>
        </Item>

        <ItemTitle>Origins 資源線路</ItemTitle>
        <ItemDesc>* 只在當前瀏覽階段生效</ItemDesc>
        <Item onClick={onClickOfficialOrigin}>
          <div style={{ whiteSpace: 'nowrap' }}>official (影片來源)</div>
          <div>
            {officialOriginName} {officialOrigin}
          </div>
        </Item>
        <Item onClick={onClickPreviewOrigin}>
          <div style={{ whiteSpace: 'nowrap' }}>preview (預覽來源)</div>
          <div>{previewOrigin}</div>
        </Item>
        <Item onClick={onClickPictureOrigin}>
          <div style={{ whiteSpace: 'nowrap' }}>picture (圖片來源)</div>
          <div>{pictureOrigin}</div>
        </Item>
        <Item onClick={onClickComicOrigin}>
          <div style={{ whiteSpace: 'nowrap' }}>comic (漫畫來源)</div>
          <div>{comicOrigin}</div>
        </Item>
        <Item onClick={onClickNovelOrigin}>
          <div style={{ whiteSpace: 'nowrap' }}>novel (小說來源)</div>
          <div>{novelOrigin}</div>
        </Item>

        <ItemTitle>Reports 報告</ItemTitle>
        <Item onClick={() => navigateToSpeedTestReport({})}>
          <div>Speed Test Report 測速報告</div>
        </Item>

        <ItemTitle>Debug flags 測試選項</ItemTitle>
        <Item onClick={clearFlags}>
          <div>清除所有測試選項</div>
        </Item>
        {DEBUG_FLAGS.map((m) => (
          <Item onClick={() => toggleFlag(m[1])} key={m[1]}>
            <div>{m[0]}</div>
            {currentFlags?.includes(m[1]) ? <div style={{ color: 'red' }}>enabled</div> : <div>disabled</div>}
          </Item>
        ))}
        <ItemTitle>Update Test 測試更新</ItemTitle>
        <Item onClick={() => dispatch(actions.updateShowMobileConfigUpdate(true))}>
          <div>顯示強制更新</div>
        </Item>
        <SafeBottomPadding />
      </PageScrollWrap>
    </PageWrapperNonScroll>
  )
}
